import {Button, Spinner} from "react-bootstrap";
import XLSX from "xlsx";
import FileSaver from "file-saver";
import {useTranslation} from "react-i18next";
import {TurnoverFilter, User} from "../models/table-models";
import dateFormat from "dateformat";
import {date} from "../utils/Enums/Common";
import {TableHeaders} from "../utils/Enums/TableHeaders";
import {getLetter} from "../utils/helper-functions";
import {useGetAllDocuments} from "../utils/users-hooks";
import {Pagination} from "../utils/pagination-hooks";
import {getDocumentsPDF, getDocumentsXML, sendArchiveEmailRange} from "../utils/clients-hooks";
import {useGetAllConfigurations} from "../utils/configuration-hooks";
import {toast} from "react-toastify";
import {CustomToastContent} from "../components/CustomToastContent";
import {toastOptionsTopRight} from "../utils/Enums/ToastOptions";
import React, {useState} from "react";

export const ExportDocuments = ({pagination, turnoversFilter, fileName, wscols, users}: {
    pagination: Pagination,
    turnoversFilter: TurnoverFilter,
    fileName: string,
    wscols: TableHeaders[],
    users: User[]
}) => {
    const {t} = useTranslation('translation', {keyPrefix: 'table'});
    const {t: common} = useTranslation('translation', {keyPrefix: 'common'});
    const [loading,setLoading]=useState(false);
    const [loadingExcel, setLoadingExcel] = useState(false);
    const [loadingXML, setLoadingXML] = useState(false);
    const [loadingArchive, setLoadingArchive] = useState(false);

    const fileType =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
    const fileExtension = ".xlsx";

    const headers = [...wscols.map((col) => t(col))];

    const calculateColumnWidths = (headers: string[], data: any[][]) => {
        return headers.map((header, colIndex) => {
            // Get max content length in column (including header)
            const maxContentWidth = Math.max(
                header.length,
                ...data.map(row => (row[colIndex] ? row[colIndex].toString().length : 0)) // Data length
            );
            return {wch: Math.max(6, maxContentWidth + 2 > 40 ? 40 : maxContentWidth + 2)}; // Ensure min width of 10
        });
    };

    const useExportToCSV = async () => {
        setLoadingExcel(true);

        const fetchDocuments = await useGetAllDocuments({
            ...pagination,
            currentPage: 1,
            pageSize: 1000000
        }, {...turnoversFilter}), csvData = fetchDocuments.items;

        const ws = XLSX.utils.aoa_to_sheet([headers]);

        const formattedData = csvData.map((item) => [
            getLetter(0, item.documentType),
            item.documentNumber,
            (item.responsibleUserIds ?? []).map(id => users.find(user => user.id === id)?.username ?? String(id)).join(', ') || '—',
            dateFormat(item.invoiceDate, date),
            item.invoicings?
                item.documentType === 'STORNO' || item.documentType === 'CREDIT_NOTE' ?
                    Number(-( item.invoicings.map(({total}) => total).reduce((a, b) => a + b, 0) / 100 ).toFixed(2))
                    :
                    Number(( item.invoicings.map(({total}) => total).reduce((a, b) => a + b, 0) / 100 ).toFixed(2))
                :0,
            t(item.paymentType)
        ]);

        XLSX.utils.sheet_add_json(ws, formattedData, {
            origin: "A2", // Ensure data starts from row 2
            skipHeader: true // Headers are already added
        });

        ws["!cols"] = calculateColumnWidths(headers, formattedData);

        const wb = {Sheets: {data: ws}, SheetNames: ["data"]};
        const excelBuffer = XLSX.write(wb, {bookType: "xlsx", type: "array"});
        const data = new Blob([excelBuffer], {type: fileType});
        FileSaver.saveAs(data, fileName + fileExtension);

        setLoadingExcel(false);
    };

    const usePrint = async () => {
        setLoading(true);

        const fetchDocuments = await useGetAllDocuments({
            ...pagination,
            currentPage: 1,
            pageSize: 1000000
        }, {...turnoversFilter}), csvData = fetchDocuments.items;

        if (csvData.length > 0) {
            const pdfFile = await getDocumentsPDF(
                csvData
            );
            if (pdfFile) {
                const pdf = URL.createObjectURL(pdfFile);
                window.open(pdf, '_blank');
                setLoading(false);
                return true;
            } else {
                toast.error(<CustomToastContent content={[`${t('printError')}`]} whiteText/>, toastOptionsTopRight);
                setLoading(false);
                return false;
            }
        }
        setLoading(false);
    };

    const useExportXML = async () => {
        setLoadingXML(true);
        const fetchDocuments = await useGetAllDocuments({
            ...pagination,
            currentPage: 1,
            pageSize: 1000000
        }, {...turnoversFilter}), documents = fetchDocuments.items.map((item) => (item.id));

        try {
            const xmlFile = await getDocumentsXML(documents);
            const url = URL.createObjectURL(xmlFile);
            const link = document.createElement("a");
            link.href = url;
            const now = new Date();

            link.download = `Sellings_${now.getDate()}${now.getMonth() + 1}${now.getFullYear()}_${now.getHours()}${now.getMinutes()}${now.getSeconds()}.xml`;

            link.click();
            URL.revokeObjectURL(url);
        } catch (error) {

        } finally {
            setLoadingXML(false);
        }
    };

    const useSendToArchive = async () => {
        setLoadingArchive(true);
        try {
            const configurations = await useGetAllConfigurations();
            const toEmail = configurations.emailInfo.emailCopy;
            const from = turnoversFilter.after;
            const to = turnoversFilter.before;
            if (!from || !to) {
                toast.error(<CustomToastContent content={[`${common('selectDateRange')}`]} whiteText/>, toastOptionsTopRight);
                return;
            }
            await sendArchiveEmailRange(from, to, toEmail);
            toast.success(<CustomToastContent content={[`${common('sendToArchiveSuccess')}`]} whiteText/>, toastOptionsTopRight);
        } catch (error) {
            toast.error(<CustomToastContent content={[`${common('sendToArchiveError')}`]} whiteText/>, toastOptionsTopRight);
        } finally {
            setLoadingArchive(false);
        }
    };

    return (
        <div className={"d-flex align-items-center"}>
            {loadingExcel ? <Spinner animation={'border'}></Spinner>
                :
                <Button
                    variant="warning"
                    className={"mx-2"}
                    onClick={useExportToCSV}
                >
                    {common('exportExcel')}
                </Button>}
            {loading ? <Spinner animation={'border'}></Spinner>
                :
                <Button
                    variant="warning"
                    className={"mx-2"}
                    onClick={usePrint}
                >
                    {common('printAllInvoices')}
                </Button>}
            {loadingXML ? <Spinner animation={'border'}></Spinner>
                :
                <Button
                    variant="warning"
                    className={"mx-2"}
                    onClick={useExportXML}
                >
                    {common('exportXML')}
                </Button>}
            {loadingArchive ? <Spinner animation={'border'}></Spinner>
                :
                <Button
                    variant="warning"
                    className={"mx-2"}
                    onClick={useSendToArchive}
                >
                    {common('sendToArchive')}
                </Button>}
        </div>
    );
};
